import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  LearnerClassCard,
  LearnerClassesResponse,
  ProgressSnapshot,
  getLearnerClasses,
  getLearnerProgress,
} from '../../api/learner';
import { KidEmpty } from '../../../../design-system/kid';
import { useLearnerSpace } from '../../learner/LearnerSpace';
import {
  formatDayAndTime,
  joinStateAt,
  useServerClock,
  waitInWords,
  zoneLabel,
} from '../../learner/learnerTime';
import { observationLineOf, showsLearnerState } from '../../learner/progressWords';

type Load =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: LearnerClassesResponse };

/**
 * UC-S07 — "Mis clases" (STU-09-FR-01..08).
 *
 * One learner's active and pending enrollments, from the server's scoped
 * read. The card's progress word is the shared projection's (lane D), not a
 * count of our own; when the projection cannot be read the card says so
 * rather than showing a remembered or invented value (S07-TR-04). Join
 * availability is a state read off the governed window, and the door itself
 * is the join screen, which checks again (S08-GT-01). There is no
 * "Comprados" tab: it was retired (PRT-D06), and its old URL lands here.
 */
const MyClasses: React.FC = () => {
  const { studentId, firstName } = useLearnerSpace();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [progress, setProgress] = useState<Record<string, ProgressSnapshot> | 'unavailable'>({});

  const fetchAll = useCallback(() => {
    if (!studentId) return;
    setLoad({ status: 'loading' });
    getLearnerClasses(studentId)
      .then((data) => setLoad({ status: 'ready', data }))
      .catch(() => setLoad({ status: 'error' }));
    getLearnerProgress(studentId)
      .then((res) => setProgress(Object.fromEntries(res.snapshots.map((s) => [s.enrollmentId, s]))))
      .catch(() => setProgress('unavailable'));
  }, [studentId]);

  useEffect(fetchAll, [fetchAll]);

  const now = useServerClock(load.status === 'ready' ? load.data.serverNow : undefined);

  return (
    <>
      <h1 className="hi">{firstName ? `Hola, ${firstName}` : 'Hola'}</h1>
      <p className="hi-s">Estas son tus clases.</p>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando tus clases…
        </p>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>No pudimos cargar tus clases</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchAll}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' && load.data.classes.length === 0 && (
        <>
          <KidEmpty
            icon="🎒"
            title="Todavía no tienes clases"
            detail="Cuando tu familia te inscriba, aparecerán aquí."
          />
          {/* STU-09-FR-07 / S07 A1 — a next step a child can take on their own:
              look around, and ask the family. Nothing to buy from here. */}
          <p className="hi-s">
            ¿Quieres aprender algo nuevo?{' '}
            <Link className="lrn-link" to="/student/explore">
              Explora clases
            </Link>
            y pídele a tu familia la que te guste.
          </p>
        </>
      )}

      {load.status === 'ready' && load.data.classes.length > 0 && (
        <>
          <p className="lrn-zone">{zoneLabel(load.data.timeZone)}</p>
          <div className="grid">
            {load.data.classes.map((card) => (
              <ClassCard
                key={card.enrollmentId}
                card={card}
                now={now}
                data={load.data}
                progress={progress === 'unavailable' ? 'unavailable' : progress[card.enrollmentId]}
              />
            ))}
          </div>
          <p className="lrn-zone">
            <Link className="lrn-link" to="/student/calendar">
              Ver mi calendario
            </Link>
          </p>
        </>
      )}
    </>
  );
};

const ClassCard: React.FC<{
  card: LearnerClassCard;
  now: Date;
  data: LearnerClassesResponse;
  progress: ProgressSnapshot | 'unavailable' | undefined;
}> = ({ card, now, data, progress }) => {
  const title = card.courseTitle ?? 'Tu clase';
  const next = card.nextSession;
  const joinState = next ? joinStateAt(next.joinWindow, now) : undefined;

  return (
    <article className="cc" aria-label={title}>
      <div className="cc-b">
        <h2 className="cc-t">{title}</h2>
        {card.teacherName && <p className="cc-w">Con tu profesor: {card.teacherName}</p>}

        {card.access === 'PENDING' ? (
          // S07-TR-06 / S07-FIN-02 — a learning-access state, never a billing one,
          // and never a Join that cannot succeed.
          <>
            <span className="lrn-tag wait">Se está preparando</span>
            <p className="cc-w">Todavía no se puede entrar. Tu familia te dirá cuándo empieza.</p>
          </>
        ) : (
          <>
            <span className="lrn-tag">Activa</span>
            {(card.cancelledSessions ?? []).map((c) => (
              // STU-16-FR-05 — said plainly, never why and never about money.
              <p className="cc-w" key={`cancelled-${c.seq}`}>
                <span className="lrn-tag wait">Cancelada</span> La clase del{' '}
                {formatDayAndTime(c.scheduledStart, data.timeZone)} se canceló.
              </p>
            ))}
            {next ? (
              <>
                <p className="cc-when">
                  Próxima clase: {formatDayAndTime(next.scheduledStart, data.timeZone)}
                  {/* The calendar's own words for the same booking state (S07-TR-05). */}
                  {next.state === 'Rescheduled' ? ' · cambió de hora' : ''}
                </p>
                {joinState === 'OPEN' || joinState === 'LATE' ? (
                  <Link className="ag-b" to={`/student/join/${encodeURIComponent(next.bookingId)}`}>
                    Entrar a la clase
                  </Link>
                ) : joinState === 'BEFORE_WINDOW' ? (
                  <p className="cc-w">
                    Faltan{' '}
                    {waitInWords(new Date(next.joinWindow.opensAt).getTime() - now.getTime())} para
                    poder entrar.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="cc-w">No hay una próxima clase en el calendario.</p>
            )}
          </>
        )}

        <p className="cc-w">
          {progress === 'unavailable'
            ? 'Lo que anotó tu profesor: no lo pudimos cargar ahora.'
            : progress && showsLearnerState(progress)
            ? observationLineOf(progress)
            : null}
        </p>

        <p>
          <Link className="lrn-link" to={`/student/courses/${encodeURIComponent(card.courseId)}`}>
            De qué trata
          </Link>
          {card.access === 'ACTIVE' && (
            <Link className="lrn-link" to="/student/progress">
              Ver cómo voy
            </Link>
          )}
        </p>
      </div>
    </article>
  );
};

export default MyClasses;
